import {useRef, useEffect, useCallback, useMemo} from 'react';
import autosuggestReducer, {useEnhancedReducer} from './reducer';
import * as stateChangeTypes from './stateChangeTypes';

import {
  normalizeArrowKey,
  handleRefs,
  scrollIntoView as defaultScrollIntoView,
  // debounce,
  callAllEventHandlers,
  // genId,
  useId,
  isOrContainsNode,
  getA11ySelectionMessage as defaultGetA11ySelectionMessage,
  getA11yStatusMessage as defaultGetA11yStatusMessage,
  setStatus,
  defaultItemToString,

  // getDefaultValue,
  // getInitialValue,
  getInitialState,
  getElementIds,
  // getState,
  // getItemIndex,
  // isAcceptedCharacterKey
} from './utils';

function noop() {}

const defaultProps = {
  itemToString: defaultItemToString,
  stateReducer: (s, a) => a.changes,
  getA11yStatusMessage: defaultGetA11yStatusMessage,
  getA11ySelectionMessage: defaultGetA11ySelectionMessage,
  scrollIntoView: defaultScrollIntoView,
  environment: typeof window === 'undefined' ? {} : window,
  onStateChange: noop,
  onInputValueChange: noop,
  onHightlightedIndexChange: noop,
  onSelectedItemChange: noop,
  onIsOpenChange: noop,
  onOuterClick: noop,
  resetOnInputBlur: true,
  resetOnOutsideClick: true,
  onShowInitialPage: false
};

useAutosuggest.stateChangeTypes = stateChangeTypes;

export default function useAutosuggest(userProps = {}) {
  const props = {
    ...defaultProps,
    ...userProps,
  };

  const {
    // items, // required prop
    itemToString, // required prop
    getA11yStatusMessage,
    getA11ySelectionMessage,
    initialIsOpen,
    defaultIsOpen,
    scrollIntoView,
    onOuterClick,
    environment,
    resetOnInputBlur,
    resetOnOutsideClick,
    onShowInitialPage
  } = props;

  const initialState = getInitialState(props);
  // IDs generation.
  const {labelId, getItemId, menuId, inputId} = getElementIds(useId, props);

  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);
  itemRefs.current = [];
  const isMouseDown = useRef(false);
  const items = useRef([]);
  items.current = [];

  const isInitialMount = useRef(true);
  const shouldScroll = useRef(true);

  // Reducer init.
  const [
    // 🐨 see what keysSoFar was in useSelect
    {isOpen, highlightedIndex, selectedItem, inputValue},
    dispatchWithoutProps,
  ] = useEnhancedReducer(autosuggestReducer, initialState, props);
  
  const dispatch = useCallback(action => dispatchWithoutProps({props, ...action}), [
    props,
    dispatchWithoutProps,
  ]);

  /** ACTIONS START */
  // 🐨 see if these can be extracted in a common
  // utility, all wrapped with 'useCallback'
  
  useEffect(() => {
    dispatchWithoutProps({
      type: stateChangeTypes.onInitialValueChange,
      props,
      inputValue: props.initialInputValue
    });
  }, [props.initialInputValue]);

  useEffect(() => {
    dispatchWithoutProps({
      type: stateChangeTypes.onInitialValueChange,
      props,
      inputValue: props.initialInputValue
    });
  }, [onShowInitialPage]);

  const reset = useCallback(
    ({becauseOf = stateChangeTypes.Reset}) => {
      dispatch({type: becauseOf});
    },
    [dispatch],
  );

  const openMenu = useCallback(() => {
    dispatch({
      type: stateChangeTypes.MenuOpen,
    });
  }, [dispatch]);

  const closeMenu = useCallback(() => {
    dispatch({
      type: stateChangeTypes.MenuClose,
    });
  }, [dispatch]);

  const toggleMenu = useCallback(() => {
    dispatch({
      type: stateChangeTypes.MenuToggle,
    });
  }, [dispatch]);

  /** ITEM SETTING ACTIONS */

  const selectItem = useCallback(
    toSelectItem => {
      dispatch({
        type: stateChangeTypes.MenuItemSelect,
        toSelectItem,
      });
    },
    [dispatch],
  );

  const selectItemAtIndex = useCallback(
    toSelectItemIndex => {
      dispatch({
        type: stateChangeTypes.MenuItemSelectAtIndex,
        toSelectItem: items.current[toSelectItemIndex],
      });
    },
    [dispatch],
  );

  const selectHighlightedIndex = useCallback(() => {
    dispatch({
      type: stateChangeTypes.MenuItemSelectAtHighlightedIndex,
    });
  }, [dispatch]);

  /** HIGHLIGHT SETTING ACTIONS */

  const setHighlightedIndex = useCallback(
    newHighlightedIndex => {
      dispatch({
        type: stateChangeTypes.SetHightlightedIndex,
        highlightedIndex: newHighlightedIndex,
      });
    },
    [dispatch],
  );

  const clearSelection = useCallback(() => {
    dispatch({
      type: stateChangeTypes.ClearSelection,
    });
  }, [dispatch]);

  const itemHandleMoveMove = useCallback(
    index => {
      if (index === highlightedIndex) {
        return;
      }

      // 🐨 understand
      // answer — helps in detecting change of highlightedIndex
      // from mouse move vs key down
      shouldScroll.current = false;
      dispatch({
        type: stateChangeTypes.ItemMouseMove,
        index,
      });
    },
    [dispatch, highlightedIndex],
  );

  const itemHandleClick = useCallback(
    (index, item) => {
      dispatch({
        type: stateChangeTypes.ItemClick,
        index,
        items: items.current,
      });
    },
    [dispatch],
  );

  const inputHandleChange = useCallback(
    event => {
      dispatch({
        type: stateChangeTypes.InputChange,
        inputValue: event.target.value, // event.nativeEvent.text for RN if need to be supported,
      });

      // setTimeout(() => {
      //   // const el = document.querySelector('input')
      //   // console.log(el);

      //   // el.focus();
      // }, 2000)
    },
    [dispatch],
  );

  const keyDownHandlers = useMemo(() => {
    return {
      ArrowDown(event) {
        event.preventDefault();
        dispatch({
          type: stateChangeTypes.KeyDownArrowDown,
          items: items.current,
          // shiftKey: event.shiftKey,
        });
      },
      ArrowUp(event) {
        event.preventDefault();
        dispatch({
          type: stateChangeTypes.KeyDownArrowUp,
          items: items.current,
          // shiftKey: event.shiftKey,
        });
      },
      Escape(event) {
        event.preventDefault();
        dispatch({
          type: stateChangeTypes.KeyDownEscape,
        });
      },
      Enter(event) {
        event.preventDefault();
        dispatch({
          type: stateChangeTypes.KeyDownEnter,
          items: items.current,
          inputFieldValue: event.target.value,
          /** 🐨  see if 'event.preventDefault()' is to be done
           * only when isOpen and highlightedIndex !== null as in Downshift.js?
           */
          // event // 🐨see if event.persist() is necessary
        });
      },
      // 🐨 see if this needs to be supported
      // Home(event) {
      //   event.preventDefault()
      //   dispatch({
      //     type: stateChangeTypes.KeyDownHome,
      //   })
      // },
      // End(event) {
      //   event.preventDefault()
      //   dispatch({KeyDownEscape
      //     type: stateChangeTypes.KeyDownEnd,
      //   })
      // },
    };
  }, [dispatch]);

  const inputKeyDownHandlers = {
    ...keyDownHandlers,
  };
  // used internally not exposed,

  const inputHandleKeyDown = useCallback(
    event => {
      const key = normalizeArrowKey(event);

      // console.log('focus current only: ', document.activeElement)
      // setTimeout(() => {
      //   console.log('focus after settimeout: ', document.activeElement)
      // })
      if (key && inputKeyDownHandlers[key]) {
        inputKeyDownHandlers[key](event);
      }
    },
    [keyDownHandlers],
  );

  // used internally not exposed
  const inputHandleBlur = useCallback(
    event => {
      // internalSetTimeout(() => {
      // 🐨 is coming undefined check what is wrong
      const autoCompleteIsActive =
        environment.document &&
        !!environment.document.activeElement &&
        !!environment.document.activeElement.dataset &&
        environment.document.activeElement.dataset.toggle &&
        rootRef.current &&
        rootRef.current.contains(environment.document.activeElement);

      if (event?.resetInput) {
        reset({
          becauseOf: stateChangeTypes.InputBlur,
        });
      }

      if (!isMouseDown?.current && !autoCompleteIsActive && resetOnInputBlur) {
        // 🐨 the difference in attribution of reducer 'types'
        // downshift's methodology to tell the user what triggered
        // a particular state change 'type' has to be done
        reset({
          becauseOf: stateChangeTypes.InputBlur,
        });
      }
      // })
    },
    [reset, resetOnInputBlur],
  );
  /** ACTIONS END */
  // const timeoutIds = useRef([]);

  // function internalSetTimeout (fn, time) {
  //   const id = setTimeout(() => {
  //     timeoutIds.current = timeoutIds.current.filter(i => i !== id)
  //     fn()
  //   }, time)

  //   timeoutIds.current.push(id);
  // }
  // const clearTimeout = useRef(null)

  const targetWithinAutocomplete = (target, checkActiveElement) => {
    const {document} = environment;
    return [rootRef.current, menuRef.current].some(
      contextNode =>
        contextNode &&
        (isOrContainsNode(contextNode, target) ||
          (checkActiveElement && isOrContainsNode(contextNode, document.activeElement))),
    );
  };

  /** EFFECTS */
  useEffect(() => {
    /* Make initial ref false. */
    isInitialMount.current = false;

    const onMouseDown = () => {
      isMouseDown.current = true;
    };

    environment.addEventListener('mousedown', onMouseDown);
    return () => {
      environment.removeEventListener('mousedown', onMouseDown);
    };
    // we want this effect to run only once – on first mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!inputValue) {
      closeMenu();
    }
  }, [inputValue]);

  useEffect(() => {
    const onMouseUp = event => {
      isMouseDown.current = false;

      const contextWithinAutocomplete = targetWithinAutocomplete(event.target);

      if (!contextWithinAutocomplete && isOpen && resetOnOutsideClick) {
        reset({
          becauseOf: stateChangeTypes.OutsideAutosuggestClick,
        });

        onOuterClick();
      }
    };

    environment.addEventListener('mouseup', onMouseUp);
    return () => {
      environment.removeEventListener('mouseup', onMouseUp);
    };
    // we only want to rerun if 'isOpen' changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resetOnOutsideClick]);

  /* Sets a11y status message on changes in isOpen. */
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    setStatus(
      getA11yStatusMessage({
        isOpen,
        items: items.current,
        selectedItem,
        itemToString,
      }),
      environment.document,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /* Sets a11y status message on changes in selectedItem. */
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    setStatus(
      getA11ySelectionMessage({
        isOpen,
        items: items.current,
        selectedItem,
        itemToString,
      }),
      environment.document,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  /** 🐨 see effect for clearing keyssofar after keyssofar usage is understood and sorted */

  // 🐨 understand timeline of useEffect
  /** Controls the focus on the menu */
  useEffect(() => {
    // Don't focus menu on first render.
    if (isInitialMount.current) {
      // Unless it was initialised as open.
      if (initialIsOpen || defaultIsOpen || isOpen) {
        menuRef.current.focus();
      }
      return;
    }
    // Focus menu on open.
    if (isOpen) {
      menuRef.current.focus();
      // Focus toggleButton on close.
    }

    // 🐨 check if focus things are correct here, while trying things out in the browser

    /** initialIsOpen, defaultIsOpen stay the same (pure props) */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /* Scroll on highlighted item if change comes from keyboard. */
  /** 🐨 see how it is only triggered on keyboard highlightedIndex */
  useEffect(() => {
    if (highlightedIndex < 0 || !isOpen || !itemRefs.current.length) {
      return;
    }
    if (shouldScroll.current === false) {
      shouldScroll.current = true;
    } else {
      scrollIntoView(itemRefs.current[highlightedIndex], menuRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedIndex]);

  /** PROP GETTERS */

  const getRootProps = useCallback(
    ({refKey = 'ref', ref, ...rest} = {}) => {
      // this is used in the render to know whether the user has called getRootProps.
      // It uses that to know whether to apply the props automatically
      return {
        [refKey]: handleRefs(ref, rootNode => {
          rootRef.current = rootNode;
        }),
        role: 'combobox',
        'aria-expanded': isOpen,
        'aria-haspopup': 'listbox',
        'aria-owns': isOpen ? menuId : null,
        'aria-labelledby': labelId,
        ...rest,
      };
    },
    [isOpen, menuId, labelId],
  );

  const getLabelProps = useCallback(
    labelProps => {
      return {
        id: labelId,
        htmlFor: inputId,
        ...labelProps,
      };
    },
    [labelId, inputId],
  );

  /**
   *
   * @param {object} [inputOptions]
   * @param {function} [inputOptions.onKeyDown]
   * @param {function} [inputOptions.onBlur]
   * @param {function} [inputOptions.onChange]
   * @param {...*} ...rest
   */
  const getInputProps = useCallback(
    inputOptions => {
      const {onKeyDown, onBlur, onChange, ...rest} = inputOptions;

      let eventHandlers = {};

      if (!rest.disabled) {
        eventHandlers = {
          onChange: callAllEventHandlers(onChange, inputHandleChange),
          onKeyDown: callAllEventHandlers(onKeyDown, inputHandleKeyDown),
          onBlur: callAllEventHandlers(onBlur, inputHandleBlur),
        };
      }

      // if react native to be supported, event handlers have to be
      // handled separately

      return {
        'aria-autocomplete': 'list',
        'aria-activedescendant':
          isOpen && typeof highlightedIndex === 'number' && highlightedIndex >= 0
            ? getItemId(highlightedIndex)
            : null,
        'aria-controls': isOpen ? menuId : null,
        'aria-labelledby': labelId,
        autoComplete: 'off',
        value: inputValue,
        id: inputId,
        ...eventHandlers,
        ...rest,
      };
    },
    [
      inputHandleChange,
      inputHandleKeyDown,
      inputHandleBlur,
      highlightedIndex,
      isOpen,
      inputValue,
      menuId,
      labelId,
    ],
  );

  const getMenuProps = useCallback(
    ({refKey = 'ref', ref, ...rest} = {}) => {
      return {
        [refKey]: handleRefs(ref, menuNode => {
          menuRef.current = menuNode;
        }),
        id: menuId,
        role: 'listbox',
        'aria-labelledby': labelId,
        ...rest,
      };
    },
    [menuId, labelId],
  );

  const getItemProps = useCallback(
    ({
      refKey = 'ref',
      ref,
      item,
      index,
      onMouseMove, // OOTB (out of the box) — internal implementation will change highlightedIndex on mouse move
      // onMouseDown, // OOTB – selection
      onClick,
      onMouseDown,
      // onPress // if react native has to be supported, but no for now
      ...rest
    }) => {
      // const itemIndex = getItemIndex(index, item, items)

      if (index === undefined) {
        items.current.push(item);
        index = items.current.indexOf(item);
      } else {
        items.current[index] = item;
      }

      const itemIndex = index;

      if (itemIndex < 0) {
        throw new Error('Pass either item or item index in getItemProps!');
      }
      return {
        [refKey]: handleRefs(ref, itemNode => {
          if (itemNode) {
            itemRefs.current.push(itemNode);
          }
        }),
        role: 'option',
        ...(itemIndex === highlightedIndex && {'aria-selected': true}),
        id: getItemId(itemIndex),
        onMouseMove: callAllEventHandlers(onMouseMove, () => {
          itemHandleMoveMove(itemIndex);
        }),
        onClick: callAllEventHandlers(onClick, () => {
          itemHandleClick(
            itemIndex,
            item, // for debug purposes remove it 🐨
          );
        }),
        onMouseDown: callAllEventHandlers(onMouseDown, event => {
          // This prevents the activeElement from being changed
          // to the item so it can remain with the current activeElement
          // which is a more common use case.
          event.preventDefault();
        }),
        ...rest,
      };
    },
    [highlightedIndex, getItemId, itemHandleMoveMove, itemHandleClick],
  );
  /** PROP GETTERS END */

  return {
    actions: {
      // misc
      reset,
      clearSelection,

      // menu
      openMenu,
      closeMenu,
      toggleMenu,

      // item
      selectItem,
      selectItemAtIndex,
      selectHighlightedIndex,

      // highlight
      setHighlightedIndex,
    },
    state: {
      highlightedIndex,
      isOpen,
      selectedItem,
      inputValue,
    },
    propGetters: {
      getLabelProps,
      getMenuProps,
      getItemProps,
      getInputProps,
      inputHandleBlur,
      getRootProps,
    },
  };
}
